// @givmo/app-charity — ACTIVE-ORG context (the single source of truth for "which charity am I managing").
//
// Retires the `memberships[0]` single-org assumption that the WEB2/WEB4/WEB5/WEB6/WEB7 features each
// hard-coded. A charity admin can belong to MULTIPLE orgs (fiscal sponsors / consultants — plan §2
// "Identity & RBAC: multi-charity per user"); this context owns:
//   - ONE `GET /charity-admin/memberships` read, shared by the shell switcher + every feature page (React
//     Query dedupes on the key → exactly one network call);
//   - the SELECTED org — persisted per-user on this device (active-org-storage) and REVALIDATED against the
//     live list — defaulting to the FIRST membership so SINGLE-ORG admins see ZERO behavior change;
//   - the active membership + its ROLE, so `isOwnerRole`-style Owner/Member gates resolve PER ACTIVE ORG.
//
// FAIL CLOSED (money + RBAC screens): `isReady` is true only once the read SUCCEEDED and an active
// membership exists — feature pages must not fire charity-scoped reads or show mutating controls before
// then. Switching org via `setActiveCharityId` re-scopes every page that reads this context.
//
// ★ APPROVAL IS ASYNCHRONOUS, SO THIS LIST GOES STALE UNDER A LIVE CONSOLE (CPA-WEB46, Jonathan
// 2026-09-07). A claim submitted from the console is approved elsewhere — a reviewer in the super-admin
// app, minutes later — and nothing in this tab pushes that news. The provider mounts ONCE, at the
// pathless authenticated layout, so `refetchOnMount` never fires again for the life of the session:
// without a deliberate trigger the switcher shows the pre-claim list until a hard reload. THREE things
// answer it, and no polling loop is one of them (an explicit ruling, not a preference):
//   1. `refetchOnWindowFocus: 'always'` — coming back to the console tab re-asks. This used to be
//      gated on `memberships.length === 0`, i.e. only the caller who administered NOTHING; the admin
//      claiming a SECOND organization — which is who Jonathan was, and the only person the switcher
//      exists for — got `false` and never refetched. That gate WAS the bug.
//   2. a short `MEMBERSHIPS_STALE_TIME` + `<MembershipsFreshness>` (memberships-freshness.tsx), which
//      re-asks on in-console navigation once that window has elapsed. The stale window is what keeps a
//      click-happy session from re-asking on every route change.
//   3. `newlyAddedOrg` — the one-time "You now administer X" moment the switcher renders, so the new
//      organization ARRIVING is visible rather than something to go and notice.
//
// ⚠ A FAILED BACKGROUND REFETCH MUST NOT UNSEAT A LIST ALREADY IN HAND. React Query moves `status` to
// `error` on a failed refetch even when the last good `data` is still there, so deriving state from
// `isError` first — as this file used to — meant one flaky focus refetch demoted a working console:
// `isReady` false, the org switcher gone (it renders nothing when not ready), the Apps page replaced by
// its retryable error. Harmless while only a membership-less caller ever refetched on focus; a real
// regression the moment (1) made that universal. So the derivation asks "do we HOLD a membership list?"
// before it asks the query's status. The fail-closed property is unchanged, because a non-empty list can
// only exist after a read succeeded.
//
// ★ AN OFF-CONTRACT 200 IS AN ERROR, NOT AN ABSENCE (CPA-WEB48). The tolerant decoder answers `[]` for
// any body it does not recognise, and `[]` was `loaded-empty` — the state that renders the onboarding
// chrome and sends the caller to `/claim`. So one shape skew (a deploy in flight, a contract drift, an
// intermediary rewriting a body) told an established admin to claim the organization they already
// administer, and recorded an empty announcement baseline on the way, so the next good read greeted
// them with news about it. The decode now has THREE answers (`decodeMemberships`): recognised-and-
// empty, recognised-and-populated, UNRECOGNISED — and the read's own `queryFn` (`membershipsQueryOptions`)
// throws `OffContractMembershipsError` on the third, so React Query treats it exactly as a failed
// request: `isError`, retried by the app's ordinary policy, the last good list (if any) kept in hand,
// "Try again" the way back. Only the first two answers can ever become `loaded-empty`, the baseline is
// written only from them (`settled`), and the portal-entry gate keeps the full shell on an error — it
// never re-onboards (`org/claim-first-gate.tsx`). ⚠ EVERY observer of this read must spread
// `membershipsQueryOptions()`: React Query runs whichever observer's `queryFn` set the query's options
// last, so a second observer built straight off the generated factory would silently reopen the door.

import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { queryOptions, useQuery, type QueryFunctionContext } from '@tanstack/react-query';
import { charityAdminListMyMembershipsOptions } from '@givmo/api-client/query';
import type { CharityMembershipOut } from '@givmo/api-client';
import { useAuth } from '../auth/auth-context';
import {
  decodeMemberships,
  extractMemberships,
  OffContractMembershipsError,
} from '../features/admins/admins-decode';
import {
  readActiveOrgId,
  readKnownOrgIds,
  writeActiveOrgId,
  writeKnownOrgIds,
} from './active-org-storage';

// Stable, module-level `select` so React Query structurally-shares the transformed result. A fresh inline
// arrow would re-run `select` every render → a new array ref every render → needless memo churn below.
// Tolerant on purpose: a body reaches `select` only after the read's `queryFn` has vetted it.
const selectMemberships = (data: unknown): CharityMembershipOut[] => extractMemberships(data);

const EMPTY_MEMBERSHIPS: CharityMembershipOut[] = [];

/**
 * How long a memberships read stays fresh (CPA-WEB46). Deliberately shorter than the app-wide 30s
 * default (`lib/query-client.ts`): this is the one read whose staleness is VISIBLE — it decides which
 * organizations the switcher offers — and the navigation refresh is rate-limited by exactly this
 * window, so a shorter one is the difference between "the new org is there when I look" and "still not
 * there". Exported so the freshness watcher and its tests read the same number.
 */
export const MEMBERSHIPS_STALE_TIME = 15_000;

/**
 * THE memberships read — the generated `GET /charity-admin/memberships` query, with the one rule every
 * observer of it must carry (CPA-WEB48): a 200 whose body `decodeMemberships` does not recognise is
 * refused in the `queryFn` (`OffContractMembershipsError`), never handed on as a list. Refusing there
 * rather than in `select` is what buys the ordinary error semantics for free — the app's retry policy,
 * `status: 'error'` with the last good `data` kept in hand, `refetch` as the retry.
 *
 * Spread this — never the bare generated factory — wherever this read is observed (the provider
 * below; the organization deep link). See the header's ⚠ for why a second observer must agree.
 */
export function membershipsQueryOptions() {
  const generated = charityAdminListMyMembershipsOptions();
  return queryOptions({
    queryKey: generated.queryKey,
    queryFn: async (context: QueryFunctionContext<typeof generated.queryKey>): Promise<unknown> => {
      // The generated factory always supplies a `queryFn`; the optional call only satisfies the option
      // type, and a missing body decodes as unrecognised like any other.
      const body: unknown = await generated.queryFn?.(context);
      if (decodeMemberships(body).kind === 'unrecognised') throw new OffContractMembershipsError();
      return body;
    },
    select: selectMemberships,
    staleTime: MEMBERSHIPS_STALE_TIME,
    // Claim approval is asynchronous and lands in ANOTHER tab (the reviewer's), so returning to this
    // one must re-ask — for every caller, not only the one who administers nothing yet. `'always'`
    // rather than `true` because the answer we are chasing can change inside the stale window, and this
    // is a single small GET on a deliberate user action, never a loop.
    refetchOnWindowFocus: 'always',
  });
}

/** The access-relevant result of the memberships read. */
export type MembershipsState = 'pending' | 'error' | 'loaded-empty' | 'ready';

export interface ActiveOrgState {
  /** Every charity the caller administers (the `GET /charity-admin/memberships` list; `[]` until loaded). */
  memberships: CharityMembershipOut[];
  /** The currently-selected org's membership (carries `role`), or `null` until positively resolved. */
  activeMembership: CharityMembershipOut | null;
  /** The active org's charity id, or `null` while the read is pending/errored/empty. */
  activeCharityId: string | null;
  /** Select a different org by charity id. Ignores an id the caller doesn't administer. */
  setActiveCharityId: (charityId: string) => void;
  /** True when the caller administers MORE THAN ONE org (switcher is interactive; else static/hidden). */
  isMultiOrg: boolean;
  /** True only once the memberships read SUCCEEDED and an active membership exists (the fail-closed gate). */
  isReady: boolean;
  /** Distinguishes an unclaimed account from an unresolved or failed memberships read. */
  membershipsState: MembershipsState;
  /** The memberships read is in flight. */
  isPending: boolean;
  /** The memberships read errored. */
  isError: boolean;
  /** The memberships read succeeded (regardless of whether any membership exists). */
  isSuccess: boolean;
  error: unknown;
  refetch: () => void;
  /**
   * An organization that appeared in the memberships list since this device last saw it — the
   * switcher's one-time "You now administer X" moment (CPA-WEB46). `null` when there is nothing to
   * announce, once acknowledged, or when the organization has since gone away again.
   */
  newlyAddedOrg: CharityMembershipOut | null;
  /** Dismiss {@link newlyAddedOrg}. The moment is raised at most once per organization per device. */
  acknowledgeNewOrg: () => void;
}

const ActiveOrgContext = createContext<ActiveOrgState | null>(null);

export function ActiveOrgProvider({ children }: { children: ReactNode }) {
  // Only the persistence KEY depends on auth (per-user bucketing); the memberships list itself is already
  // user-scoped by the backend. Reading it via `useAuth` keeps this compatible with the feature tests that
  // partial-mock the auth module.
  const { user, retryBackendUserBootstrap } = useAuth();
  const uid = user?.uid ?? null;

  const membershipsQuery = useQuery(membershipsQueryOptions());
  const memberships = membershipsQuery.data ?? EMPTY_MEMBERSHIPS;
  const { isPending, isError, isSuccess, error, refetch: queryRefetch } = membershipsQuery;

  // The caller's EXPLICIT in-session selection (their intent). Seeded from this device's persisted choice;
  // `null` means "no explicit choice yet" → default to the first membership. Always revalidated against the
  // live list at derivation time (below), so a stale / no-longer-a-member id self-heals to the default.
  const [selectedId, setSelectedId] = useState<string | null>(() => readActiveOrgId(uid));

  // Resolve the ACTIVE membership SYNCHRONOUSLY: the selected org if it's still a live membership, else the
  // FIRST (single-org default → zero behavior change). Deriving during render (no effect) means there is no
  // first-commit flash where a resolved list has no active org, and no test/timing race.
  const activeMembership = useMemo<CharityMembershipOut | null>(() => {
    if (memberships.length === 0) return null;
    const chosen =
      selectedId !== null ? memberships.find((m) => m.charity_id === selectedId) : undefined;
    return chosen ?? memberships[0] ?? null;
  }, [memberships, selectedId]);

  // Holding a list beats the query's status (see the header's ⚠): a non-empty list can only come from a
  // read that succeeded, so answering `ready` off it never opens a door that was closed — it only stops
  // a failed BACKGROUND refetch from closing one that was already open. `loaded-empty` is reachable ONLY
  // through a body the read recognised (CPA-WEB48): an unrecognised 200 never becomes `data` — the
  // `queryFn` refuses it, so it arrives here as `isError`, like any failed request.
  const membershipsState: MembershipsState =
    memberships.length > 0 ? 'ready' : isPending ? 'pending' : isError ? 'error' : 'loaded-empty';

  const setActiveCharityId = useCallback(
    (charityId: string) => {
      // Ignore an id the caller doesn't administer (defends against a stale switcher option).
      if (!memberships.some((m) => m.charity_id === charityId)) return;
      setSelectedId(charityId);
      writeActiveOrgId(uid, charityId);
    },
    [memberships, uid],
  );

  // ── The one-time "You now administer X" moment (CPA-WEB46) ──────────────────────────────────────
  //
  // Detected HERE, above the portal-entry gate, rather than in the switcher: the switcher does not exist
  // for a caller who administers nothing (the gate renders the onboarding chrome instead), so a first
  // approval would be detected only after the chrome had already changed under it. The provider sees
  // every state.
  //
  // The baseline is REPLACED with the live list each time the read positively answers, never unioned
  // into it. That is what makes Jonathan's second repro work — deactivate → re-claim → approve: the
  // deactivation trims the organization out of the baseline, so its return is news again. A union would
  // remember it forever and the re-claim would land in silence.
  //
  // ⚠ THE BASELINE MOVES ONLY ON A RECOGNISED ANSWER (CPA-WEB48). `settled` is `ready` or
  // `loaded-empty`, and an unrecognised 200 reaches neither — the read's `queryFn` refuses it as an
  // error — so one off-contract body can no longer record an empty baseline and have the NEXT good
  // read announce an organization the caller has had all along. (Before the cure, the tolerant
  // decoder's `[]`-for-anything met `loaded-empty` here and, worse, at the portal-entry gate.)
  //
  // Raised at most ONCE per organization per device, because the baseline advances at the moment of
  // detection, not on acknowledgement: a moment nobody dismissed must not come back on every reload.
  const membershipIds = memberships.map((m) => m.charity_id).join('|');
  const settled = membershipsState === 'ready' || membershipsState === 'loaded-empty';
  const [newOrgId, setNewOrgId] = useState<string | null>(null);

  useEffect(() => {
    // Only a POSITIVE answer moves the baseline. A pending or errored read would otherwise record an
    // empty list and re-announce every organization the caller has when the read recovers.
    if (!settled) return;
    const current = membershipIds.length === 0 ? [] : membershipIds.split('|');
    const known = readKnownOrgIds(uid);
    writeKnownOrgIds(uid, current);
    if (known === null) return; // First sight on this device — seed the baseline, announce nothing.
    const added = current.find((id) => !known.includes(id));
    if (added !== undefined) setNewOrgId(added);
  }, [settled, membershipIds, uid]);

  // Resolved against the LIVE list, so an announcement whose organization has since gone away (or that
  // belongs to a previous signed-in user) clears itself rather than naming a membership nobody holds.
  const newlyAddedOrg = useMemo<CharityMembershipOut | null>(
    () => (newOrgId === null ? null : (memberships.find((m) => m.charity_id === newOrgId) ?? null)),
    [memberships, newOrgId],
  );
  const acknowledgeNewOrg = useCallback(() => setNewOrgId(null), []);

  const value = useMemo<ActiveOrgState>(
    () => ({
      memberships,
      activeMembership,
      activeCharityId: activeMembership?.charity_id ?? null,
      setActiveCharityId,
      isMultiOrg: memberships.length > 1,
      // Off `membershipsState`, not raw `isSuccess`, for the reason in the header: a list in hand keeps
      // the console open across a failed background refetch.
      isReady: membershipsState === 'ready' && activeMembership !== null,
      membershipsState,
      isPending,
      isError,
      isSuccess,
      error,
      // "Try again" first re-runs the user bootstrap (a no-op when it already succeeded — the
      // per-uid memo — but the real retry when the auth-time ensure failed on a flaky network and
      // published a degraded session; see ensure-backend-user.ts), then refetches. Without this,
      // retrying a rowless 403 refetches into the same 403 forever. It runs through the auth
      // context's `retryBackendUserBootstrap`, which also re-establishes the suite session once the
      // row exists (AS-SUITE11 — a bootstrap healed here used to leave the account without one).
      refetch: () => {
        void (
          user !== null ? retryBackendUserBootstrap().catch(() => undefined) : Promise.resolve()
        ).then(() => void queryRefetch());
      },
      newlyAddedOrg,
      acknowledgeNewOrg,
    }),
    [
      memberships,
      activeMembership,
      setActiveCharityId,
      isSuccess,
      membershipsState,
      isPending,
      isError,
      error,
      queryRefetch,
      user,
      retryBackendUserBootstrap,
      newlyAddedOrg,
      acknowledgeNewOrg,
    ],
  );

  return <ActiveOrgContext value={value}>{children}</ActiveOrgContext>;
}

/** Read the active-org state. Throws if used outside an {@link ActiveOrgProvider}. */
export function useActiveOrg(): ActiveOrgState {
  const ctx = use(ActiveOrgContext);
  if (ctx === null) {
    throw new Error('useActiveOrg must be used within an <ActiveOrgProvider>.');
  }
  return ctx;
}

export { ActiveOrgContext };
